/**
 * Pastilles de couleur d'une carte produit.
 * Maison Scarlett : au clic, la carte passe sur la variante choisie (photo, prix, badges, liens, ajout rapide)
 * sans quitter la page. Repli sur la redirection si les données des variantes sont absentes.
 */
class ProductCardSwatches extends HTMLElement {
  connectedCallback() {
    this.card = this.closest('product-card');
    this.optionIndex = Number(this.dataset.optionIndex || 0);
    this.variants = this.#readVariants();
    this.selectedVariant = this.variants.find((variant) => String(variant.id) === this.dataset.selectedVariantId);
    this.#applyCardVariant();

    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
  }

  #readVariants() {
    const script = this.querySelector('[data-ref="variants-data"]');
    if (!script) return [];

    try {
      return JSON.parse(script.textContent);
    } catch {
      return [];
    }
  }

  /**
   * Grille « une carte par couleur » (sections/main-collection.liquid) : la carte affiche la couleur indiquée
   * par le <script data-card-variant-id> qui la suit. Le script inline a déjà placé la photo sur la 1re page ;
   * pour les cartes ajoutées par « Voir plus » ou les filtres (scripts non exécutés), le placement se fait ici.
   */
  #applyCardVariant() {
    if (!this.card) return;

    const marker = this.card.nextElementSibling;
    if (!this.card.dataset.cardVariantId && marker?.matches('script[data-card-variant-id]')) {
      this.card.dataset.cardVariantId = marker.dataset.cardVariantId;
      if (marker.dataset.cardColorIndex) {
        this.card.dataset.cardColorIndex = marker.dataset.cardColorIndex;
        this.#updateTitle(marker.dataset.cardColor);
      }
      this.#placeMedia(marker.dataset.cardMediaId);
    }

    const variantId = this.card.dataset.cardVariantId;
    if (!variantId || variantId === String(this.selectedVariant?.id)) return;

    const variant = this.variants.find((candidate) => String(candidate.id) === variantId);
    if (!variant) return;

    const value = variant.options[this.optionIndex];
    this.querySelectorAll('[data-href]').forEach((element) => {
      if (element.dataset.value === value) {
        element.setAttribute('aria-checked', 'true');
      } else {
        element.removeAttribute('aria-checked');
      }
    });

    this.selectedVariant = variant;
    if (!this.card.querySelector('slider-component')) this.#updateMedia(variant);
    this.#updatePrice(variant);
    this.#updateBadges(variant);
    this.#updateLinks(variant);
  }

  /** Met la photo de la couleur en 1re position, avant ou après l'initialisation du slider. */
  #placeMedia(mediaId) {
    if (!mediaId) return;

    const slide = this.card.querySelector(`.splide__slide[data-media-id="${mediaId}"]:not(.splide__slide--clone)`);
    if (!slide) return;

    const slider = this.card.querySelector('slider-component');
    if (slider?.slider) {
      const slides = [...slider.querySelectorAll('.splide__slide:not(.splide__slide--clone)')];
      const index = slides.indexOf(slide);
      if (index > 0) slider.slider.go(index);
    } else if (slide.previousElementSibling) {
      slide.parentElement.prepend(slide);
    }
  }

  #onClick = (event) => {
    const swatch = event.target.closest('[data-href]');
    if (!swatch) return;

    event.preventDefault();
    event.stopPropagation();
    this.#select(swatch);
  };

  #onKeydown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    const swatch = event.target.closest('[data-href]');
    if (!swatch) return;

    event.preventDefault();
    event.stopPropagation();
    this.#select(swatch);
  };

  #select(swatch) {
    const variant = this.#findVariant(swatch.dataset.value);

    if (!variant || !this.card) {
      window.location.href = swatch.dataset.href;
      return;
    }

    this.querySelectorAll('[data-href]').forEach((element) => {
      if (element === swatch) {
        element.setAttribute('aria-checked', 'true');
      } else {
        element.removeAttribute('aria-checked');
      }
    });

    this.selectedVariant = variant;
    this.#updateMedia(variant);
    this.#updatePrice(variant);
    this.#updateBadges(variant);
    this.#updateLinks(variant);

    const colorIndex = this.card.dataset.cardColorIndex;
    if (colorIndex !== undefined) this.#updateTitle(variant.options[Number(colorIndex)]);
  }

  /** Grille « une carte par couleur » : « Titre du produit - couleur ». */
  #updateTitle(color) {
    const title = this.card.querySelector('h2, h3');
    if (!title || !color) return;

    if (!title.dataset.baseTitle) title.dataset.baseTitle = title.textContent.trim();
    title.textContent = `${title.dataset.baseTitle} - ${color.toLowerCase()}`;
  }

  /**
   * Variante ayant la couleur choisie, en gardant si possible les autres options de la variante courante,
   * sinon la première disponible, sinon la première tout court.
   */
  #findVariant(value) {
    const withValue = this.variants.filter((variant) => variant.options[this.optionIndex] === value);
    if (withValue.length === 0) return null;

    const current = this.selectedVariant;
    if (current) {
      const sameOtherOptions = withValue.find((variant) =>
        variant.options.every((option, index) => index === this.optionIndex || option === current.options[index]),
      );
      if (sameOtherOptions) return sameOtherOptions;
    }

    return withValue.find((variant) => variant.available) || withValue[0];
  }

  #updateMedia(variant) {
    if (!variant.media_id) return;

    const slider = this.card.querySelector('slider-component');
    if (slider?.slider) {
      const slides = [...slider.querySelectorAll('.splide__slide:not(.splide__slide--clone)')];
      const index = slides.findIndex((slide) => slide.dataset.mediaId === String(variant.media_id));
      if (index >= 0) slider.slider.go(index);
      return;
    }

    const singleMedia = this.card.querySelector('[data-ref="single-media"]');
    const image = singleMedia?.querySelector('img');
    if (!image || !variant.image_src) return;

    image.src = variant.image_src;
    image.srcset = variant.image_srcset;
    singleMedia.dataset.mediaId = String(variant.media_id);
  }

  #updatePrice(variant) {
    const price = variant.price;
    const compareAtPrice = variant.compare_at_price || 0;

    this.card.querySelectorAll('product-price').forEach((productPrice) => {
      productPrice.updatePrice?.({
        detail: {
          prices: {
            price,
            compare_at_price: compareAtPrice,
            difference_price: Math.max(compareAtPrice - price, 0),
          },
        },
      });
    });
  }

  #updateBadges(variant) {
    const soldOutBadge = this.card.querySelector('[data-badge="sold-out"]');
    if (soldOutBadge) soldOutBadge.hidden = variant.available;

    const salesBadge = this.card.querySelector('[data-badge="sales"]');
    if (salesBadge) salesBadge.hidden = !(variant.compare_at_price > variant.price);
  }

  #updateLinks(variant) {
    this.card.dataset.productUrl = variant.url;
    this.card.productUrl = variant.url;

    this.card.querySelectorAll('a[href]').forEach((link) => {
      const url = new URL(link.href, window.location.origin);
      if (url.pathname === new URL(variant.url, window.location.origin).pathname) {
        link.href = variant.url;
      }
    });

    const quickAddToggle = this.card.querySelector('[data-ref="popup-toggle"][data-product-page-url]');
    if (quickAddToggle) quickAddToggle.dataset.productPageUrl = variant.url;
  }
}

if (!customElements.get('product-card-swatches')) {
  customElements.define('product-card-swatches', ProductCardSwatches);
}
